import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import {
  serviceItemService,
  ServiceItemDTO,
  CreateServiceItemRequest,
} from '../../../../services/serviceItemService';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { getServerErrorMessage } from '../../../../utils/errorUtils';

const emptyForm = (): CreateServiceItemRequest => ({
  code: '',
  name: '',
  defaultDescription: '',
  defaultPrice: 0,
  allowPriceChange: true,
  allowDescriptionChange: true,
  taxRate: 0,
  isActive: true,
  sortOrder: 0,
});

const ServiceItemsPage: React.FC = () => {
  const { t } = useTranslation();

  const [items,        setItems]        = useState<ServiceItemDTO[]>([]);
  const [loading,      setLoading]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [success,      setSuccess]      = useState<string | null>(null);
  const [editingId,    setEditingId]    = useState<number | 'new' | null>(null);
  const [form,         setForm]         = useState<CreateServiceItemRequest>(emptyForm());
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  useEffect(() => { loadItems(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await serviceItemService.getAll();
      const data = (res?.data as any)?.data ?? res?.data ?? [];
      if (Array.isArray(data)) setItems(data);
    } catch {
      setError(t('serviceItems.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setForm(emptyForm());
    setEditingId('new');
    setError(null);
  };

  const startEdit = (item: ServiceItemDTO) => {
    setForm({
      code: item.code,
      name: item.name,
      defaultDescription: item.defaultDescription || '',
      defaultPrice: item.defaultPrice,
      allowPriceChange: item.allowPriceChange,
      allowDescriptionChange: item.allowDescriptionChange,
      taxRate: item.taxRate,
      isActive: item.isActive,
      sortOrder: item.sortOrder,
    });
    setEditingId(item.id);
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      setError('Code and Name are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editingId === 'new') {
        await serviceItemService.create(form);
      } else if (editingId != null) {
        await serviceItemService.update(editingId, form);
      }
      setSuccess(t('serviceItems.savedSuccess'));
      setEditingId(null);
      await loadItems();
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? t('serviceItems.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setSaving(true);
    try {
      await serviceItemService.delete(id);
      setSuccess(t('serviceItems.deletedSuccess'));
      setConfirmDelete(null);
      await loadItems();
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? t('serviceItems.failedToDelete'));
    } finally {
      setSaving(false);
    }
  };

  const field = (key: keyof CreateServiceItemRequest, label: string, type = 'text', extra?: React.InputHTMLAttributes<HTMLInputElement>) => (
    <div className="ims-form-group" style={{ marginBottom: 10 }}>
      <label className="ims-form-label" style={{ fontSize: 12 }}>{label}</label>
      <input
        type={type}
        className="ims-form-control"
        value={form[key] as string | number}
        onChange={(e) => setForm((prev) => ({ ...prev, [key]: type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
        {...extra}
      />
    </div>
  );

  return (
    <Layout>
      <div style={{ padding: '0 20px 40px', maxWidth: 900, margin: '0 auto' }}>

        <div className="ims-list-header">
          <h1 className="ims-list-title">{t('serviceItems.title')}</h1>
          <div className="ims-list-actions">
            <button className="btn-new" onClick={startNew} disabled={editingId != null}>
              <Plus size={16} strokeWidth={2} />
              {t('serviceItems.addNew')}
            </button>
          </div>
        </div>

        {success && (
          <div className="ims-alert ims-alert-success" style={{ marginBottom: 16 }}>
            <Check size={15} /> {success}
            <button type="button" onClick={() => setSuccess(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
          </div>
        )}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>
            <X size={15} /> {error}
            <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
          </div>
        )}

        {/* ── Inline form for new / edit ── */}
        {editingId != null && (
          <div className="ims-card" style={{ marginBottom: 20 }}>
            <div className="ims-card-header">
              <h2 className="ims-card-title">{editingId === 'new' ? t('serviceItems.createTitle') : t('serviceItems.editTitle')}</h2>
            </div>
            <div className="ims-card-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                {field('code', t('serviceItems.code'), 'text', { placeholder: t('serviceItems.codePlaceholder'), maxLength: 50 })}
                {field('name', t('serviceItems.name'), 'text', { placeholder: t('serviceItems.namePlaceholder'), maxLength: 100 })}
                {field('defaultPrice', t('serviceItems.defaultPrice'), 'number', { min: 0, step: 0.01 })}
                {field('taxRate', t('serviceItems.taxRate'), 'number', { min: 0, max: 100, step: 0.01 })}
                {field('sortOrder', t('serviceItems.sortOrder'), 'number', { min: 0, step: 1 })}
              </div>
              <div className="ims-form-group" style={{ marginBottom: 10 }}>
                <label className="ims-form-label" style={{ fontSize: 12 }}>{t('serviceItems.defaultDescription')}</label>
                <textarea
                  className="ims-form-control ims-textarea"
                  rows={2}
                  value={form.defaultDescription || ''}
                  placeholder={t('serviceItems.descriptionPlaceholder')}
                  onChange={(e) => setForm((prev) => ({ ...prev, defaultDescription: e.target.value }))}
                />
              </div>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={form.allowPriceChange}
                    onChange={(e) => setForm((prev) => ({ ...prev, allowPriceChange: e.target.checked }))} />
                  {t('serviceItems.allowPriceChange')}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={form.allowDescriptionChange}
                    onChange={(e) => setForm((prev) => ({ ...prev, allowDescriptionChange: e.target.checked }))} />
                  {t('serviceItems.allowDescriptionChange')}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={form.isActive}
                    onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))} />
                  {t('serviceItems.isActive')}
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="ims-btn ims-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('common.saving')}</> : t('common.save')}
                </button>
                <button className="ims-btn ims-btn-secondary" onClick={cancelEdit} disabled={saving}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── List ── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
          </div>
        ) : items.length === 0 ? (
          <div className="ims-card" style={{ textAlign: 'center', padding: 40, color: 'var(--ims-text-muted)' }}>
            {t('serviceItems.noItems')}
          </div>
        ) : (
          <div className="ims-card">
            <div className="ims-table-scroll">
              <table className="ims-table">
                <thead>
                  <tr>
                    <th className="ims-th" style={{ width: 90 }}>{t('serviceItems.code')}</th>
                    <th className="ims-th">{t('serviceItems.name')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 110 }}>{t('serviceItems.defaultPrice')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 90 }}>{t('serviceItems.taxRate')}</th>
                    <th className="ims-th" style={{ width: 70 }}>{t('serviceItems.isActive')}</th>
                    <th className="ims-th" style={{ width: 100 }} />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="ims-tr">
                      <td className="ims-td"><code>{item.code}</code></td>
                      <td className="ims-td">
                        <div style={{ fontWeight: 600 }}>{item.name}</div>
                        {item.defaultDescription && (
                          <div style={{ fontSize: 11, color: 'var(--ims-text-muted)' }}>{item.defaultDescription}</div>
                        )}
                      </td>
                      <td className="ims-td ims-td--line-total">{item.defaultPrice.toFixed(2)}</td>
                      <td className="ims-td ims-td--line-total">{item.taxRate}%</td>
                      <td className="ims-td">
                        <span className={`ims-badge ${item.isActive ? 'ims-badge-completed' : 'ims-badge-closed'}`}>
                          {item.isActive ? t('common.active') : t('common.inactive')}
                        </span>
                      </td>
                      <td className="ims-td">
                        {confirmDelete === item.id ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="ims-btn ims-btn-danger" style={{ padding: '2px 8px', fontSize: 12 }}
                              onClick={() => handleDelete(item.id)} disabled={saving}>
                              {t('common.confirm')}
                            </button>
                            <button className="ims-btn ims-btn-secondary" style={{ padding: '2px 8px', fontSize: 12 }}
                              onClick={() => setConfirmDelete(null)}>
                              {t('common.cancel')}
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <button className="grid-action-btn-edit" title={t('common.edit')} onClick={() => startEdit(item)}>
                              <Pencil size={15} strokeWidth={1.75} />
                            </button>
                            <button className="grid-action-btn-delete" title={t('common.delete')} onClick={() => setConfirmDelete(item.id)}>
                              <Trash2 size={15} strokeWidth={1.75} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ServiceItemsPage;
